(() => {
  'use strict';
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
  }).format(cents / 100);
  const root = () => window.Shopify?.routes?.root || '/';
  const findVariant = (variants, values) => variants.find(variant =>
    variant.options.length === values.length && variant.options.every((value, index) => value === values[index]));
  const priceLabels = (card, now = window.CheapUndiesDeal?.now() ?? Date.now()) => {
    const deadline = Date.parse(card.dataset.cardDealEnds || '');
    const activeDeal = Number.isFinite(deadline) && deadline > now;
    return { current: activeDeal ? 'Today' : 'Our Price', comparison: activeDeal ? 'Was' : 'Compare-at' };
  };
  const optionName = option => (typeof option === 'string' ? option : option?.name || '').trim().toLowerCase();
  const fixedOptionValue = (product, index) => {
    const option = product.options[index];
    const name = optionName(option);
    // Include sold-out variants: availability must not turn multiple choices into one.
    const values = [...new Set(product.variants.map(variant => variant.options[index]))];
    if (values.length !== 1) return null;
    if (['color', 'colour', 'colors', 'colours'].includes(name)) return values[0];
    if (name !== 'size') return null;
    const label = String(values[0]).toLowerCase().replace(/[\s_-]/g, '');
    return ['os', 'onesize', 'onesizefitsall'].includes(label) ? values[0] : null;
  };
  // Earlier options narrow later ones, without making another colour impossible
  // merely because the currently selected size is unavailable in that colour.
  const valueAvailable = (variants, values, index, value) => variants.some(variant =>
    variant.available && variant.options[index] === value &&
    variant.options.slice(0, index).every((previous, previousIndex) => (values[previousIndex] === null || previous === values[previousIndex])));

  const init = form => {
    if (form.dataset.cardPickerReady) return;
    const card = form.closest('.product-card');
    const input = form.querySelector('[data-card-variant-input]');
    const add = form.querySelector('[data-card-add]');
    const status = form.querySelector('[data-card-status]');
    if (!card || !input || !add || !status) return;
    let product;
    try { product = JSON.parse(form.querySelector('[data-card-product-json]').textContent); } catch (_) { return; }
    if (!Array.isArray(product.variants) || !product.variants.length) return;
    form.dataset.cardPickerReady = 'true';
    const groups = [...form.querySelectorAll('[data-card-option-index]')];
    const initial = product.variants.find(variant => variant.id === product.selected_variant_id) || product.variants.find(variant => variant.available) || product.variants[0];
    let values = groups.length ? initial.options.map(() => null) : [...initial.options];
    let busy = false;
    let currentVariant;
    let userSelectedOption = false;
    const tell = (message, error = false) => { status.textContent = message; status.dataset.error = String(error); };
    const paintPrice = variant => {
      const price = card.querySelector('.card-price');
      if (!price) return;
      if (card.hasAttribute('data-home-deal-card') || card.hasAttribute('data-scheduled-deal-card')) {
        let data = {};
        try { data = JSON.parse(card.querySelector('[data-home-return-prices]')?.textContent || '{}'); } catch (_) {}
        const labels = priceLabels(card);
        const future = data.variants?.[String(variant.id)];
        const now = window.CheapUndiesDeal?.now() ?? Date.now();
        const showTomorrow = labels.current === 'Today' && now < Date.parse(data.valid_until || '') && data.currency === (window.Shopify?.currency?.active || 'USD') && future?.current === variant.price && Number.isInteger(future.return) && future.return > variant.price;
        const block = (label, amount, tag, className) => {
          const node = document.createElement('div'); node.className = className;
          const name = document.createElement('small'); name.textContent = label;
          const value = document.createElement(tag); value.textContent = amount;
          node.append(name, value); return node;
        };
        const nodes = [block(labels.current, variant.formatted_price || money(variant.price), 'strong', 'card-price-current')];
        if (showTomorrow) {
          const tomorrow = block('Tomorrow', money(future.return), 'b', 'home-card-tomorrow');
          tomorrow.dataset.homeReturnUntil = data.valid_until; nodes.push(tomorrow);
        }
        const retail = Number(variant.compare_at_price);
        const showRetail = Number.isInteger(retail) && retail > variant.price;
        if (showRetail) nodes.push(block('Retail', variant.formatted_compare_at_price || money(retail), 's', 'card-price-compare'));
        const savings = document.createElement('span'); savings.className = 'home-deal-discounts';
        if (showRetail) { const retailSaving = document.createElement('span'); retailSaving.textContent = `${Math.floor((retail - variant.price) * 100 / retail)}% off retail`; savings.append(retailSaving); }
        if (showTomorrow) { const tomorrowSaving = document.createElement('span'); tomorrowSaving.dataset.homeReturnUntil = data.valid_until; tomorrowSaving.textContent = `Save ${Math.floor((future.return - variant.price) * 100 / future.return)}% vs Tomorrow`; savings.append(tomorrowSaving); }
        if (savings.childNodes.length) nodes.push(savings);
        price.replaceChildren(...nodes);
        return;
      }
      const wasFocused = price.contains(document.activeElement);
      const current = document.createElement('div');
      current.className = 'card-price-current';
      const label = document.createElement('small');
      const labels = priceLabels(card);
      label.textContent = labels.current;
      const amount = document.createElement('strong');
      amount.textContent = variant.formatted_price || money(variant.price);
      current.append(label, amount);
      const nodes = [current];
      if (variant.compare_at_price > variant.price) {
        const comparison = document.createElement('div');
        comparison.className = 'card-price-compare';
        const compareLabel = document.createElement('small');
        compareLabel.textContent = labels.comparison;
        const compareAmount = document.createElement('s');
        compareAmount.textContent = variant.formatted_compare_at_price || money(variant.compare_at_price);
        comparison.append(compareLabel, compareAmount);
        nodes.push(comparison);
      }
      price.replaceChildren(...nodes);
      if (wasFocused) add.focus();
    };
    const render = () => {
      groups.forEach((group, index) => {
        const fixedValue = fixedOptionValue(product, index);
        if (fixedValue !== null) values[index] = fixedValue;
        else if (group.hidden) values[index] = null;
        group.hidden = fixedValue !== null;
      });
      currentVariant = findVariant(product.variants, values);
      groups.forEach((group, index) => {
        group.querySelectorAll('[data-card-option-value]').forEach(button => {
          const available = valueAvailable(product.variants, values, index, button.dataset.cardOptionValue);
          button.disabled = busy || !available;
          button.setAttribute('aria-pressed', String(values[index] === button.dataset.cardOptionValue));
          const visibleLabel = button.textContent.trim();
          const accessibleLabel = visibleLabel === button.dataset.cardOptionValue ? visibleLabel : `${visibleLabel} (${button.dataset.cardOptionValue})`;
          button.setAttribute('aria-label', `${accessibleLabel}${available ? '' : ' — sold out'}`);
        });
      });
      input.value = currentVariant?.id || '';
      card.dataset.cardVariantId = input.value;
      const soldOut = !product.variants.some(variant => variant.available);
      card.classList.toggle('sold-out', soldOut);
      const stamp = card.querySelector('.card-sold-overlay');
      if (stamp) stamp.hidden = !soldOut;
      card.querySelectorAll(':scope > .product-card-image, :scope > .product-card-body').forEach(content => {
        content.inert = soldOut;
        if (soldOut) content.setAttribute('aria-hidden', 'true');
        else content.removeAttribute('aria-hidden');
      });
      add.disabled = busy || !currentVariant?.available;
      const visibleGroups = groups.filter(group => !group.hidden);
      add.textContent = busy ? 'Adding…' : soldOut ? 'Sold Out' : !currentVariant ? (visibleGroups.length === 1 && optionName(product.options[Number(visibleGroups[0].dataset.cardOptionIndex)]) === 'size' ? 'Select A Size' : 'Choose Options') : !currentVariant.available ? 'Sold Out' : 'Add To Drawer';
      if (product.requires_selling_plan) { add.disabled = busy; add.textContent = 'View Options'; }
      form.setAttribute('aria-busy', String(busy));
      if (currentVariant) {
        paintPrice(currentVariant);
        const image = card.querySelector('.product-card-image img');
        if (image && currentVariant.featured_image?.src && (userSelectedOption || card.dataset.preferSecondImage !== 'true')) {
          const source = new URL(currentVariant.featured_image.src, location.href);
          if (source.pathname.startsWith('/cdn/shop/') || source.hostname === 'cdn.shopify.com') {
            const atWidth = width => { const url = new URL(source); url.searchParams.set('width', String(width)); return url.href; };
            image.srcset = [240,360,540,720].map(width => `${atWidth(width)} ${width}w`).join(', ');
            image.src = atWidth(540);
          } else { image.removeAttribute('srcset'); image.src = source.href; }
          image.alt = currentVariant.featured_image.alt || product.title;
        }
        const colorIndex = product.options.findIndex(option => ['color', 'colour', 'colors', 'colours'].includes(optionName(option)));
        const color = card.querySelector('.card-color');
        if (color && colorIndex !== -1) color.textContent = values[colorIndex] || '';
        card.querySelectorAll('.product-card-image, h3 a').forEach(link => {
          const url = new URL(product.url, location.origin);
          url.searchParams.set('variant', currentVariant.id);
          link.href = url.pathname + url.search;
        });
      } else {
        paintPrice(product.variants.find(variant => variant.available) || product.variants[0]);
        card.querySelectorAll('.product-card-image, h3 a').forEach(link => { link.href = product.url; });
      }
    };
    const adoptProduct = fresh => {
      if (!Array.isArray(fresh.variants)) throw new Error('Product information unavailable.');
      product = { ...product, ...fresh, options: fresh.options || product.options };
      // Keep the user's exact option choices when inventory/prices refresh.
      render();
    };
    groups.forEach((group, index) => group.addEventListener('click', event => {
      const button = event.target.closest('[data-card-option-value]');
      if (!button || button.disabled || busy) return;
      userSelectedOption = true;
      values[index] = button.dataset.cardOptionValue;
      for (let next = index + 1; next < values.length; next++) {
        if (!valueAvailable(product.variants, values, next, values[next])) values[next] = null;
      }
      render();
      const missing = values.findIndex(value => value === null);
      const option = product.options[missing];
      tell(missing !== -1 ? `Choose ${typeof option === 'string' ? option : option?.name || 'an option'}.` : currentVariant?.available ? `${currentVariant.options.join(' / ')} selected.` : 'This combination is unavailable.');
    }));
    card.addEventListener('card:product-refreshed', event => {
      if (!event.detail?.product || busy) return;
      try { adoptProduct(event.detail.product); } catch (_) { /* Keep existing data; submit still revalidates. */ }
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy) return;
      if (product.requires_selling_plan) { location.assign(product.url); return; }
      if (!currentVariant?.available) { tell('Choose an available combination before adding.', true); return; }
      const selectedId = currentVariant.id;
      const displayedPrice = currentVariant.price;
      busy = true;
      render();
      tell('Checking current price and availability…');
      let addRequested = false;
      try {
        const current = await fetch(`${root()}products/${encodeURIComponent(product.handle)}.js?_=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!current.ok) throw new Error('Could not check availability. Please try again.');
        const fresh = await current.json();
        adoptProduct(fresh);
        if (product.requires_selling_plan) { tell('View this product to choose its purchase options.'); return; }
        const confirmed = product.variants.find(variant => variant.id === selectedId);
        if (!confirmed?.available) { tell('This selection is now sold out. Choose another option.', true); return; }
        if (confirmed.price !== displayedPrice) { tell(`The price is now ${money(confirmed.price)}. Review it, then select Add To Drawer again.`); return; }
        tell('Adding to your drawer…');
        addRequested = true;
        const response = await fetch(`${root()}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: confirmed.id, quantity: 1 }] }) });
        const result = await response.json();
        if (!response.ok) {
          addRequested = false;
          throw new Error(typeof result.description === 'string' ? result.description : 'This selection could not be added. Please check its availability.');
        }
        const item = result.items?.[0] || result;
        tell('Added to your drawer.');
        form.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'product-card', confirmedAdd: true, item, openDrawer: true } }));
      } catch (error) {
        tell(addRequested ? 'Could not confirm the add. Check your drawer before trying again.' : error.message || 'Could not add this selection. Please try again.', true);
      } finally {
        busy = false;
        render();
      }
    });
    render();
    form.hidden = false;
    const fallback = card.querySelector('[data-card-picker-fallback]');
    if (fallback) fallback.hidden = true;
  };
  let claimExpiryTimer;
  const initWithin = scope => {
    scope.querySelectorAll('[data-card-picker]').forEach(form => {
      try { init(form); }
      catch (error) { console.error('Product card could not initialize:', error); }
    });
    if (!claimExpiryTimer && document.querySelector('[data-scheduled-deal-card]')) {
      claimExpiryTimer = setInterval(() => {
        const cards = document.querySelectorAll('[data-scheduled-deal-card]');
        if (!cards.length) { clearInterval(claimExpiryTimer); claimExpiryTimer = null; return; }
        const now = window.CheapUndiesDeal?.now() ?? Date.now();
        cards.forEach(card => card.querySelectorAll('[data-home-return-until]').forEach(claim => {
          if (!(now < Date.parse(claim.dataset.homeReturnUntil)) || !(now < Date.parse(card.dataset.cardDealEnds))) claim.remove();
        }));
      }, 1000);
    }
  };
  initWithin(document);
  document.addEventListener('shopify:section:load', event => initWithin(event.target));
})();
